import React, { useContext, useEffect, useState } from 'react'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import MicroModal from 'micromodal'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { useParams } from 'react-router-dom'
import { ColumnDef, ColumnFiltersState, SortingState } from '@tanstack/react-table' 
import { TeamContext } from '../../MenuTabs/MenuTabs'
import useLicenseTable from '../context/LicenseTableContext'
import LicensesTableButton from './LicensesTableButton'
import { modalManageLicenseId } from './Utils'
import ModalManageLicense from '../ManageLicense/ModalManageLicense'
import { LicenseTableData, LicenseTableType } from './data/LicenseTableData'
import ManageLicense from '../../../../../orgs/ManageLicense'

const LicensesTable = ({ licensesData }) => {

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([])
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => LicenseTableData( licensesData ) )
  const { setUseHeader, refetchLicenses } = useLicenseTable()

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader(['all'])
  }, [])

  const columns = React.useMemo<ColumnDef<LicenseTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'licenseTypeID',
            accessorKey: 'licenseTypeID',
            prettyName: 'License Type ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>LICENSE TYPE ID</span>,
          },
          {
            id: 'name',
            accessorKey: 'name',
            prettyName: 'Name',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>NAME</span>,
          },
          {
            id: 'product',
            accessorKey: 'product',
            prettyName: 'Product',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>PRODUCT</span>,
          },
          {
            id: 'total',
            accessorKey: 'total',
            prettyName: 'Total',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>TOTAl</span>,
          },
          {
            id: 'assigned',
            accessorKey: 'assigned',
            prettyName: 'Assigned',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>ASSIGNED</span>,
          },
          {
            id: 'productID',
            accessorKey: 'productID',
            prettyName: 'Product ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>PRODUCT ID</span>,
          },
          {
            id: 'content',
            accessorKey: 'content',
            prettyName: 'Content',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>CONTENT</span>,
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={ info.row.original } />
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const Actions = ({ info }) => {
    return (
      <div className='msync-table-action-column'>
        <ManageLicense 
          name={info.name}
          productID={info.productID}
          licenseTypeID={info.licenseTypeID}
          assigned={info.assigned}
          total={info.total}
          membersAssigned={info.membersAssigned}
          content={info.content}
        />
        <LicensesTableButton
          btnName='Manage'
          id={ modalManageLicenseId( info.productID, info.licenseTypeID ) }
          info={ info }
          teamType={'msynclicenses'}
          btnClassName="msync-button msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline"
        />
      </div>
    )
  }

  const exportHeaders = getExportHeaders('team-licenses')
  const { teamData } = useContext(TeamContext)

  return (
    <>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Licenses table'
        context='Licenses'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
        {/* <BulkModalConfirmation key='modal-member-bulk-confirmation-message' /> */}
        <ModalManageLicense key='modal-member-manage-license'/>
        {/* <ModalConfirmation key='modal-member-confirmation-message'/> */}
      </TanstackContent>
    </>
  )
}

export default LicensesTable
